import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Paper
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function EmployerJobsPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);

    // First, get the user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Then load jobs for that company
    const { data: jobsData, isLoading: jobsLoading, isError, error } = useQuery(
        ['employerJobs', company?.id, page],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadJobs({
                page,
                size: 12,
                filters: { company: company.id, archived: '0' } // Only show active (non-expired) jobs
            });
        },
        {
            enabled: !!company?.id,
            keepPreviousData: true
        }
    );

    const jobs = jobsData?.results || [];
    const totalPages = Math.ceil((jobsData?.count || 0) / 12);

    const handleJobClick = (jobId) => {
        navigate(`/employer/jobs/${jobId}`);
    };

    if (companyLoading || jobsLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h4" fontWeight={600} gutterBottom>
                                My Jobs
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your job postings
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<WorkIcon />}
                            onClick={() => navigate('/employer/jobs/new')}
                        >
                            Post New Job
                        </Button>
                    </Box>

                    {isError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error?.message || 'Failed to load jobs'}
                        </Alert>
                    )}

                    {jobs.length === 0 && !jobsLoading && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                No jobs found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Start by posting your first job
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/employer/jobs/new')}
                            >
                                Post New Job
                            </Button>
                        </Paper>
                    )}

                    <Grid container spacing={3}>
                        {jobs.map((job) => (
                            <Grid item xs={12} key={job.id}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 4
                                        }
                                    }}
                                    onClick={() => handleJobClick(job.id)}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                                    {job.title}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                    {job.company && (
                                                        <Chip
                                                            icon={<BusinessIcon />}
                                                            label={job.company.title}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {job.address && (
                                                        <Chip
                                                            icon={<LocationOnIcon />}
                                                            label={job.address}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {job.jobtypes && job.jobtypes.length > 0 && (
                                                        <Chip
                                                            icon={<WorkIcon />}
                                                            label={job.jobtypes.map(jt => jt.title).join(', ')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                                {job.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            mb: 2,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {job.description}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <CalendarTodayIcon fontSize="small" />
                                                        <Typography variant="caption">
                                                            {new Date(job.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                    {job.pay_minimum && job.pay_maximum && (
                                                        <Typography variant="caption">
                                                            ${job.pay_minimum} - ${job.pay_maximum}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            <ArrowForwardIcon sx={{ color: 'primary.main', ml: 2 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
                            <Button
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2" sx={{ alignSelf: 'center', px: 2 }}>
                                Page {page} of {totalPages}
                            </Typography>
                            <Button
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next
                            </Button>
                        </Box>
                    )}
                </Container>
            </section>
    );
}

export default EmployerJobsPage;

